import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    OnChanges,
    SimpleChanges,
    Output,
    SecurityContext,
    ViewEncapsulation
} from "@angular/core";
import {ITCIMarkupEditorContext, TCI_MARKUP_DISPLAY_MODE, TCIMarkupEditorDisplaySize} from "@tci/common";
import {EditorInstance, EditorLocale, EditorOption} from "./lib/angular-markdown-editor";
import {FormBuilder, FormGroup} from "@angular/forms";
import {MarkdownService} from "ngx-markdown";
import {TranslateService} from "@ngx-translate/core";
import {DomSanitizer} from "@angular/platform-browser";

export interface ISantizeResult {
    sanitizeError: boolean;
    sanitizeErrorMsg: string;
}

@Component({
    selector: "ng-markdown-editor",
    templateUrl: "./ng-markdown-editor.component.html",
    styleUrls: ["./ng-markdown-editor.component.less"],
    encapsulation: ViewEncapsulation.None,
})
export class NgMarkdownEditorComponent implements OnChanges, OnInit, AfterViewInit {

    // To avoid DOM exception: mutiple textareas with non unique Id found
    @Input() textareaId = "editor1";

    @Input() editorContext: ITCIMarkupEditorContext;

    @Input() mode: TCI_MARKUP_DISPLAY_MODE;

    @Input() displaySize: TCIMarkupEditorDisplaySize;

    @Output() editorContextChange = new EventEmitter<ITCIMarkupEditorContext>();

    @Input() cssClasses?: string | string[] = [];

    /** Markdown Editor Options to pass to the element */
    @Input() options?: EditorOption;

    /**
     * Markdown Editor locale Dictionary
     */
    @Input() locale: EditorLocale = {
        language: "en",
        dictionary: {
            "Bold": "Bold",
            "Italic": "Italic",
            "Heading": "Heading",
            "URL/Link": "URL/Link",
            "Image": "Image",
            "List": "List",
            "Ordered List": "Ordered List",
            "Unordered List": "Unordered List",
            "Code": "Code",
            "Quote": "Quote",
            "Preview": "Preview",
            "Strikethrough": "Strikethrough",
            "Table": "Table",
            "strong text": "strong text",
            "emphasized text": "emphasized text",
            "heading text": "heading text",
            "enter link description here": "enter link description here",
            "Insert Hyperlink": "Insert Hyperlink",
            "enter image description here": "enter image description here",
            "Insert Image Hyperlink": "Insert Image Hyperlink",
            "enter image title here": "enter image title here",
            "list text here": "list text here"
        }
    };

    @Input() numLines = 25;
    /**
     * toggle expand behavior
     */
    @Input() canExpand = true;

    /**
     * Implement this to notify the parent to do any subscription or pre-init activity
     * usage: (onBeforeShow)="doBeforeShow()"
     */
    @Output() beforeShow: EventEmitter<ITCIMarkupEditorContext> = new EventEmitter<ITCIMarkupEditorContext>();
    /**
     * Implement this to notify the parent to do clean up subscription before hiding
     * (onAfterHide)="doAfterHide()"
     */
    @Output() afterHide: EventEmitter<ITCIMarkupEditorContext> = new EventEmitter<ITCIMarkupEditorContext>();


    bsEditorInstance: EditorInstance;
    // markdownText: string;
    showEditor = true;
    templateForm: FormGroup;
    editorMode: boolean;
    editorOptions: EditorOption;
    editorLocale: EditorLocale;
    sanitizeError: boolean;
    sanitizeErrorMsg: string;
    fullScreenMode = false;

    constructor(
        private fb: FormBuilder,
        private markdownService: MarkdownService,
        private translate: TranslateService,
        private sanitizer: DomSanitizer,
        @Inject("markDownConfig") private markdownConfig?: EditorOption,
        @Inject("markDownLocale") private clocale?: EditorLocale) {
        this.locale = clocale;
        translate.getDefaultLang();
        translate.get("NG_MARKDOWN_EDITOR");
    }

    ngOnChanges(changes: SimpleChanges) {
        // Update the Editor Textarea when reset is called from the parent
        const editorContext = changes.editorContext;
        let textareaContent;
        if (changes.editorContext !== undefined && this.templateForm) {
            textareaContent = this.templateForm.get("body");
            if (editorContext.currentValue.content !== textareaContent.value) {
                textareaContent.setValue(editorContext.currentValue.content);
            }
        }
    }

    ngOnInit() {
        const defaultEditorOptions = {
            autofocus: false,
            iconlibrary: "fa",
            savable: false,
            onFullscreenExit: (e) => this.hidePreview(e),
            onShow: (e) => {
                this.bsEditorInstance = e;
                // Editor will be in Edit Mode by default
                e.hideButtons("cmdEdit");
            },
            parser: (val) => this.parse(val)
        };
        this.editorOptions = {...defaultEditorOptions, ...this.markdownConfig, ...this.options};
        this.editorLocale = {...this.locale};
        this.editorMode = this.mode === TCI_MARKUP_DISPLAY_MODE.MODE_EDIT;
        this.buildForm(this.editorContext);
        this.onFormChanges();
    }

    ngAfterViewInit() {

    }

    sanitizeData(content: string): ISantizeResult {
        const sanitizeResult = {
            sanitizeError: false,
            sanitizeErrorMsg: ""
        } as ISantizeResult;
        let data = content;
        // data = this.sanitizer.sanitize(SecurityContext.SCRIPT, content);
        data = this.sanitizer.sanitize(SecurityContext.HTML, content);
        if (data === "unsafe") {
            sanitizeResult.sanitizeError = true;
            sanitizeResult.sanitizeErrorMsg = "Failed HTML check";
            return sanitizeResult;
        }
        // data = this.sanitizer.sanitize(SecurityContext.STYLE, data);
        // if (data === "unsafe") {
        //     sanitizeResult.sanitizeError = true;
        //     sanitizeResult.sanitizeErrorMsg = "Failed STYLE check";
        //     return sanitizeResult;
        // }
        // data = this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, data);
        data = this.sanitizer.sanitize(SecurityContext.URL, data);
        if (data === "unsafe") {
            sanitizeResult.sanitizeError = true;
            sanitizeResult.sanitizeErrorMsg = "Failed URL check";
            return sanitizeResult;
        }
        return sanitizeResult;
    }

    buildForm(context: ITCIMarkupEditorContext) {
        this.templateForm = this.fb.group({
            body: [context.content],
            isPreview: [true]
        });
    }

    /** highlight all code found, needs to be wrapped in timer to work properly */
    highlight() {
        setTimeout(() => {
            this.markdownService.highlight();
        }, 0);
    }

    hidePreview(e) {
        if (this.bsEditorInstance && this.bsEditorInstance.hidePreview) {
            this.bsEditorInstance.hidePreview();
        }
    }

    showFullScreen(isFullScreen: boolean) {
        if (this.bsEditorInstance && this.bsEditorInstance.setFullscreen) {
            this.bsEditorInstance.showPreview();
            this.bsEditorInstance.setFullscreen(isFullScreen);
        }
    }

    parse(inputValue: string) {
        const markedOutput = this.markdownService.compile(inputValue.trim());
        const sanitizeRes = this.sanitizeData(markedOutput);
        if (sanitizeRes.sanitizeError) {
            return sanitizeRes.sanitizeErrorMsg;
        } else {
            this.highlight();
            return markedOutput;
        }
    }

    onFormChanges(): void {
        this.templateForm.valueChanges.subscribe(formData => {
            this.sanitizeError = false;
            if (formData) {
                const sanitizeRes = this.sanitizeData(formData.body);
                if (sanitizeRes.sanitizeError) {
                    this.sanitizeError = sanitizeRes.sanitizeError;
                    this.sanitizeErrorMsg = sanitizeRes.sanitizeErrorMsg;
                } else {
                    this.editorContext.content = formData.body.trim();
                    this.editorContextChange.emit(this.editorContext);
                }
            }
        });
    }

    toggleFullScreen() {
        this.fullScreenMode = !this.fullScreenMode;
    }

}
